import React, { FC, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, Dispatch } from "../../store";
import { object, string } from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import DialogLayout from "../DialogLayout";
import Typography from "../../components/Typography/Typography";
import ASSETS from "../../assets";
import TextField from "../../components/TextField/TextField";
import Button from "../../components/Button/Button";
import { authAPI, UpdateUserRequest } from "../../services/api";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const schema = object({
  firstName: string()
    .required("First name is required")
    .max(20, "First name cannot exceed 20 characters")
    .matches(
      /^[A-Za-z\s]*$/,
      "First name cannot contain numbers or special characters"
    ),

  lastName: string()
    .required("Last name is required")
    .max(20, "Last name cannot exceed 20 characters")
    .matches(
      /^[A-Za-z\s]*$/,
      "Last name cannot contain numbers or special characters"
    ),
});

const Profile: FC<IProps> = ({ open, setOpen }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
    },
    validationSchema: schema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const updateData: UpdateUserRequest = {
          firstName: values.firstName,
          lastName: values.lastName,
        };

        const response = await authAPI.updateUser(updateData);
        
        // Show success message
        toast.success(response.message || 'Profile updated successfully!');
        
        // Close modal
        handleClose();
        
        // Refresh user profile in layout
        await dispatch.auth.fetchUserProfileAsync();
        
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to update profile. Please try again.';
        toast.error(errorMessage);
        // Modal remains open on error
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    setOpen(false);
    setHasChanges(false);
  };

  // Track changes to enable/disable save button
  useEffect(() => {
    if (user) {
      const originalFirstName = user.firstName || "";
      const originalLastName = user.lastName || "";
      
      const hasFirstNameChanged = formik.values.firstName !== originalFirstName;
      const hasLastNameChanged = formik.values.lastName !== originalLastName;
      
      setHasChanges(hasFirstNameChanged || hasLastNameChanged);
    }
  }, [formik.values.firstName, formik.values.lastName, user]);

  useEffect(() => {
    if (user) {
      formik.setValues({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
      });
      setHasChanges(false);
    }
    // eslint-disable-next-line
  }, [user]);

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[536px] p-[25px] bg-white rounded-[15px] relative">
        <div
          className="absolute top-[25px] right-[25px] rounded-full size-[30px] bg-[#0000000D] cursor-pointer grid place-items-center"
          onClick={handleClose}
        >
          <img src={ASSETS.cross} alt="" />
        </div>
        <Typography variant="h2" className="font-bold title">
          Profile
        </Typography>
        <Typography
          variant="body1"
          color="grey"
          className="mt-[5px] font-medium"
        >
          Edit your name and copy your wallet address. To make a change to your
          email address, please contact your organization’s admin.
        </Typography>
        <div className="border border-k-border rounded-lg p-6 mt-[25px] flex flex-col gap-6">
          <TextField
            label="First Name"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.firstName && formik.errors.firstName
                ? formik.errors.firstName
                : ""
            }
            onClear={() => formik.setFieldValue("firstName", "")}
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.lastName && formik.errors.lastName
                ? formik.errors.lastName
                : ""
            }
            onClear={() => formik.setFieldValue("lastName", "")}
          />
          <TextField label="Email" value={user?.email || ""} readOnly />
       
          <Button
            label={loading ? "Saving..." : "Save Changes"}
            variant="primary"
            className="w-full h-[45px] disabled:opacity-200 disabled:cursor-not-allowed  disabled:!text-gray-400"
            onClick={() => formik.handleSubmit()}
            loading={loading}
            disabled={!hasChanges || loading}
          />
        </div>
      </div>

    </DialogLayout>
  );
};

export default Profile;
